import Image from "next/image";
import logo from "@public/logo-black.png";
import RiveAnimation from "@/components/ui/rive-animation.dynamic";
import HeaderSearch from "./header-search";
import LocaleOption from "./locale-option";
import dynamic from "next/dynamic";
import { Fragment, useState, useEffect } from "react";
import { Transition } from "@headlessui/react";
import { Dialog } from "@headlessui/react";
import {
  ArrowLongRightIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useRouter } from "next/router";
import SearchBar from "@components/search/search-bar";
import {
  sharedLinks,
  selectedIndexForHamburger,
  topNavLinks,
  generateDomainPath,
  redirectedBrowserTab,
  SocialMediaIcons,
} from "./shared/link-props";
import Link from "next/link";
import { useWindowWidth } from "@react-hook/window-size";
import { Trans } from "@lingui/macro";
import { useMainBannerContext } from "@lib/mainBannerContext";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import HeroCarousel from "@components/landing-page/hero-carousel";

export const STATIC_PAGES = [
  "/lumi-parliament-playground",
  "/contact-us",
  "/404",
  "/500",
  "/pdpa",
  "/privacy-policy",
  "/t&c",
  "/publisher-application",
  "/colony2026",
  "/baristar2026",
];

const NewsStacksCarousel = dynamic(() => import("./news-stack-carousel"));

interface IHeaderProps {
  showNewsStacks?: boolean;
  isNewsStacksSticky?: boolean;
  setFocusInput?: React.Dispatch<React.SetStateAction<boolean>>;
  focusInput?: boolean;
  hideMagnifyingGlassIcon?: boolean;
  hideLocaleOptions?: boolean;
}

const Header: React.FunctionComponent<IHeaderProps> = ({
  showNewsStacks = true,
  isNewsStacksSticky,
  setFocusInput,
  focusInput,
  hideMagnifyingGlassIcon,
  hideLocaleOptions,
}) => {
  const router = useRouter();
  const width = useWindowWidth();
  const { locale, pathname, asPath } = router;
  const kHeroCarouselItems = [
    {
      href: {
        en: "/topics/state_polls",
        ms: "/topics/state_polls",
        zh: "/topics/state_polls",
      }[locale],
      desktopUrl: `https://images.luminews.my/lumi-prn-melaka-2026-web-banner-desktop-v2.webp`,
      mobileUrl: `https://images.luminews.my/lumi-prn-melaka-2026-web-banner-mobile-v2.webp`,
      alt: "PRN Melaka 2026",
    },
    {
      href: {
        en: "/topics/haze",
        ms: "/topics/haze",
        zh: "/topics/haze",
      }[locale],
      mobileUrl: `https://images.luminews.my/lumi_haze_banner_${locale}_web_mobile.webp`,
      desktopUrl: `https://images.luminews.my/lumi_haze_banner_${locale}_web.webp`,
      alt: "haze-custom-tab-banner",
    },
    {
      href: {
        en: "/news-stack/baristar_rm1_coffee_lumi_news_campaign_2026",
        ms: "/news-stack/kempen_kopi_rm1_baristar_lumi_news_2026",
        zh: "/news-stack/baristar_rm1_%E5%92%96%E5%95%A1_lumi_news_2026_%E6%B4%BB%E5%8A%A8",
      }[locale],
      desktopUrl: `https://images.luminews.my/lumi-baristar-2026-web-banner-desktop-${locale}.png`,
      mobileUrl: `https://images.luminews.my/lumi-baristar-2026-web-banner-mobile-${locale}-v2.png`,
      alt: "baristar2026",
    },
  ];
  const currentYear = new Date().getFullYear();

  const [isOpen, setIsOpen] = useState(false);

  const [isBannerUnderCarousel, setIsBannerUnderCarousel] = useState(false);
  const showMainBanner = useMainBannerContext();
  const resolvedPath = router.asPath.replace(/\?[^#]*/, "").toLowerCase();

  useEffect(() => {
    if (showNewsStacks && isNewsStacksSticky && width <= 768) {
      setIsBannerUnderCarousel(true);
    } else {
      setIsBannerUnderCarousel(false);
    }
  }, [width, showNewsStacks, isNewsStacksSticky]);

  useEffect(() => {
    if (width >= 1024 && isOpen) {
      setIsOpen(false);
    }
  }, [width, isOpen]);

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  // To open a new tab if logo is clicked or search input is entered on subdomain pages
  const showNewTab = pathname.startsWith("/_subdomain");

  // To remove last scroll position of home if home logo is clicked
  const clearHomeSession = () => {
    sessionStorage.removeItem(`scrollPos:/`);
  };

  const selectedLocale =
    (locale || router.defaultLocale) === "en" ? "" : locale;

  const isFifaTopic = resolvedPath.toLowerCase().includes("/topics/fifa_2026");

  return (
    <>
      <div className="mb-3 flex flex-col">
        <nav aria-label="Global">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 pt-8">
            <div onClick={clearHomeSession}>
              {!isFifaTopic ? (
                <Link href={generateDomainPath(`/${locale}`)}>
                  <a target={showNewTab ? "_blank" : undefined}>
                    <Image
                      src={logo}
                      height={24}
                      width={104}
                      alt="logo"
                      priority
                    />
                  </a>
                </Link>
              ) : (
                <Link href={generateDomainPath(`/${locale}`)} key="">
                  <a>
                    <RiveAnimation
                      src="/Splash_Screen_Web.riv"
                      autoBind
                      repeat
                      className="w-[100px] md:w-[150px]"
                    />
                  </a>
                </Link>
              )}
            </div>

            <div className="hidden space-x-6 lg:flex lg:justify-center">
              {topNavLinks.map((link) =>
                link.external ? (
                  <a
                    href={
                      link.withLocale ? link.href + selectedLocale : link.href
                    }
                    key={link.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="px-4 py-2 font-sen text-text-medium hover:text-text-dark">
                      {link.emoji} {link.title}
                    </div>
                  </a>
                ) : (
                  <div key={link.href}>
                    {redirectedBrowserTab(
                      link,
                      asPath,
                      showNewTab,
                      locale,
                      "header"
                    )}
                  </div>
                )
              )}
            </div>

            <div className="flex items-center space-x-1 2xs:space-x-8">
              {!hideLocaleOptions && <LocaleOption />}

              {/* REMOVE AFTER GE15 */}
              {!hideMagnifyingGlassIcon && (
                <HeaderSearch
                  setFocusInput={setFocusInput}
                  focusInput={focusInput}
                  showNewTab={showNewTab}
                />
              )}

              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary lg:hidden">
                <Bars3Icon
                  className="h-5 w-5 cursor-pointer text-primary2"
                  aria-hidden="true"
                  onClick={openModal}
                />
              </div>

              <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                  <div className="fixed inset-0">
                    <div className="h-full">
                      <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="translate-x-full"
                        enterTo="translate-x-"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-full"
                      >
                        <Dialog.Panel className="h-full w-full bg-white p-6 text-left first-letter:overflow-hidden">
                          <XMarkIcon
                            onClick={closeModal}
                            className="ml-auto mr-0 mb-10 h-5 w-5 text-primary2"
                          />

                          <div className="mb-10">
                            <SearchBar />
                          </div>

                          <div>
                            {topNavLinks
                              // only shows prn link on sidebar
                              .filter((link) => !link.href.includes("prn"))
                              .slice(0, selectedIndexForHamburger)
                              .map((link) =>
                                link.external ? (
                                  <a
                                    href={
                                      link.withLocale
                                        ? link.href + selectedLocale
                                        : link.href
                                    }
                                    key={link.href}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <div className="mb-4">
                                      <span className="font-sen text-text-medium">
                                        {link.emoji} {link.title}
                                      </span>
                                    </div>
                                  </a>
                                ) : (
                                  <div
                                    key={link.href}
                                    onClick={
                                      link.href.includes("/news") &&
                                      pathname == "/news"
                                        ? closeModal
                                        : null
                                    }
                                  >
                                    {redirectedBrowserTab(
                                      link,
                                      asPath,
                                      showNewTab,
                                      locale,
                                      "hamburgerMainHeader"
                                    )}
                                  </div>
                                )
                              )}
                          </div>

                          <div className="mb-6">
                            <div
                              className={`mx-auto mt-8 flex items-start justify-start text-primary4 lg:mt-0 lg:justify-start`}
                            >
                              <SocialMediaIcons tiktokColor="blue" />
                            </div>
                          </div>

                          <div className="mb-6 flex flex-wrap items-center">
                            {sharedLinks.slice(0, -2).map((link) =>
                              link.external ? (
                                <a
                                  key={link.href}
                                  href={
                                    link.withLocale
                                      ? link.href + selectedLocale
                                      : link.href
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span className="mr-4 mb-4 font-sen text-xs text-text-medium">
                                    {link.emoji} {link.title}
                                  </span>
                                </a>
                              ) : (
                                <div key={link.href}>
                                  {redirectedBrowserTab(
                                    link,
                                    asPath,
                                    showNewTab,
                                    locale,
                                    "hamburgerBtmHeader"
                                  )}
                                </div>
                              )
                            )}
                          </div>

                          <p className="font-sen text-xs text-disabled">
                            © Copyright Lumi {currentYear}
                          </p>
                          <p className="mt-2 font-sen text-xs text-disabled">
                            Lumi Global Sdn Bhd (1407575-K)
                          </p>
                          <div className="mt-4 flex flex-row space-x-4">
                            <div
                              className={`flex items-center font-sen text-sm text-disabled`}
                            >
                              <EnvelopeIcon
                                className="mr-1 h-5 w-6"
                                aria-hidden="true"
                              />
                              <a href="mailto:hello@luminews.my">
                                hello@luminews.my
                              </a>
                            </div>
                            <div
                              className={`flex items-center font-sen text-sm text-disabled`}
                            >
                              <PhoneIcon
                                className="mr-1 h-5 w-6"
                                aria-hidden="true"
                              />
                              <a href="tel:+60123395864">+60 12-339 5864</a>
                            </div>
                          </div>
                        </Dialog.Panel>
                      </Transition.Child>
                    </div>
                  </div>
                </Dialog>
              </Transition>
            </div>
          </div>
        </nav>
      </div>
      {showNewsStacks &&
        (isNewsStacksSticky ? (
          <div className="sticky top-0 z-30 mt-4 bg-white px-4 md:relative">
            <NewsStacksCarousel />
          </div>
        ) : (
          <div className="mx-4 mt-4">
            <NewsStacksCarousel />
          </div>
        ))}
      {showMainBanner && (
        <a href={`/${locale}/topics/parliament_malaysia_2026`}>
          <div
            className={`sticky ${
              isBannerUnderCarousel ? "top-14" : "top-0"
            } z-20 flex items-center justify-center space-x-2 bg-primary p-4`}
          >
            <p className="cursor-pointer font-sen text-white underline">
              <Trans>Which MP are you? 🤔 Take the MPTI quiz</Trans>
            </p>
            <ArrowLongRightIcon className="h-4 w-4 text-white" />
          </div>
        </a>
      )}
      {
        // thin blue banner code sample
        // typeof window !== "undefined" &&
        // window?.location?.host != "about.luminews.my" &&
        // !STATIC_PAGES.includes(router.pathname) &&
        // !resolvedPath.toLowerCase().includes("/topics/fifa_2026") &&
        // !resolvedPath.toLowerCase().includes("/topics/state_polls") && (
        //   <Link href="/topics/state_polls" id="ad-banner">
        //     <a id="ad-banner">
        //       <div
        //         className={`sticky ${
        //           isBannerUnderCarousel ? "top-14" : "top-0"
        //         } z-30 flex items-center justify-center space-x-2 bg-primary p-4`}
        //       >
        //         <p className="cursor-pointer font-sen text-white underline">
        //           {locale == "zh"
        //             ? "🗳️ 森美兰州选举即将开跑！8月1日开票，最新资讯都在这里 👉"
        //             : locale == "ms"
        //             ? "🗳️ AKAN DATANG: PRN Negeri Sembilan pada 1 Ogos. Semua kemas kini 👉"
        //             : "🗳️ COMING UP: Negeri Sembilan State Elections on 1 August. All the updates 👉"}
        //         </p>
        //         <ArrowLongRightIcon className="h-4 w-4 text-white" />
        //       </div>
        //     </a>
        //   </Link>
        // )
      }
      <HeroCarousel initialIndex={0} items={kHeroCarouselItems} />
    </>
  );
};

export default Header;
